import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert, Tab, Tabs } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../../layouts/PageTitle';
import { strapiUrl } from '../../../../utils/constants';
import { axiosPost } from '../../../../utils/axiosPost';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { useNotifications } from '../../../hooks/useNotifications';
import { useChairReportStatuses } from '../../../../context/ChairReportStatusesContext';

import ChairReminderLogs from '../ChairReport/ChairReminderLogs';
import ModalConfirmation from '../../ModalConfirmation';
import LazyCommitteeCard from './LazyCommitteeCard';
import RankingsSummary from './RankingsSummary';
import useRankingReport from './hooks/useRankingReport';
import useCommitteeEdit from './hooks/useCommitteeEdit';
import { devError } from '../../../../utils/devLog';

const RankingReport = () => {
  const navigate = useNavigate();

  const { isAdmin, isSCChair, isCouncilMember, isSCSecretary } =
    useUserPermissions();

  const {
    notificationMessage,
    notificationTitle,
    notificationIcon,
    notificationIconColor,
    notificationAction,
    showNotification,
  } = useNotifications();

  const {
    reportStatuses,
    allReportsSubmitted,
    statusesLoading,
    refetchStatuses,
  } = useChairReportStatuses();

  const {
    loading,
    error,
    visibleReportData,
    committeeMembersMap,
    csvData,
    isChairOfCommittee,
  } = useRankingReport();

  const {
    editingCommitteeId,
    setEditingCommitteeId,
    showSwitchWarningModal,
    setShowSwitchWarningModal,
    handleRequestEdit,
    handleConfirmSwitch,
    handleCancelSwitch,
    handleDirtyChange,
  } = useCommitteeEdit();

  const [isGeneratingAll, setIsGeneratingAll] = useState(false);

  const handleDownloadAll = useCallback(async () => {
    setIsGeneratingAll(true);
    try {
      const response = await axiosPost('chair-report-pdf/generate-all', {});
      if (response?.status === 200 && response.data?.url) {
        window.open(`${strapiUrl}${response.data.url}`, '_blank');
      }
    } catch (err) {
      devError('Failed to generate all chair report PDFs:', err);
    } finally {
      setIsGeneratingAll(false);
    }
  }, []);

  const councilMemberBlocked =
    isCouncilMember &&
    !isSCSecretary &&
    !statusesLoading &&
    !allReportsSubmitted;

  useEffect(() => {
    if (councilMemberBlocked) {
      navigate('/permission-denied');
    }
  }, [councilMemberBlocked, navigate]);

  useEffect(() => {
    if (!visibleReportData.length || !isSCChair) return;

    const chairCommitteeIds = [];
    visibleReportData.forEach((committee) => {
      if (isChairOfCommittee(committee)) {
        chairCommitteeIds.push(
          Number(committee.sectionalCommitteeId || committee.id),
        );
      }
    });

    // If the chair only has one form, auto-enter edit mode
    if (chairCommitteeIds.length === 1) {
      setEditingCommitteeId(chairCommitteeIds[0]);
    }
  }, [visibleReportData, isSCChair, isChairOfCommittee, setEditingCommitteeId]);

  if ((isCouncilMember && statusesLoading) || councilMemberBlocked) {
    return (
      <div className="d-flex justify-content-center align-items-center ranking-report-loader">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center ranking-report-loader">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <PageTitle
          motherMenu="Administration"
          activeMenu="Ranked Candidates Report"
          notificationMessage={notificationMessage}
          notificationTitle={notificationTitle}
          notificationIcon={notificationIcon}
          notificationIconColor={notificationIconColor}
          notificationAction={notificationAction}
        />
        <Alert variant="danger">{error}</Alert>
      </>
    );
  }

  const rankingsContent = (
    <>
      <div className="mb-4 d-flex justify-content-end gap-2">
        {allReportsSubmitted &&
          !isSCChair &&
          (isAdmin || isCouncilMember || isSCSecretary) && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleDownloadAll}
              disabled={isGeneratingAll}
            >
              <i
                className={`fas ${isGeneratingAll ? 'fa-sync fa-spin' : 'fa-file-archive'} me-2`}
              />
              {isGeneratingAll ? 'Generating...' : 'Download All Chair Reports'}
            </button>
          )}
        <CSVLink
          data={csvData}
          filename={`ranked-candidates-${new Date().toISOString().split('T')[0]}.csv`}
          className="btn btn-primary"
        >
          <i className="fas fa-download me-2" />
          Download Ranking Data
        </CSVLink>
      </div>

      <RankingsSummary
        visibleReportData={visibleReportData}
        reportStatuses={reportStatuses}
        statusesLoading={statusesLoading}
      />

      {visibleReportData.length === 0 ? (
        <Alert variant="info">No ranked candidates</Alert>
      ) : (
        visibleReportData.map((committee, idx) => {
          const committeeId = Number(
            committee.sectionalCommitteeId || committee.id,
          );
          const committeeMembers = committeeMembersMap[committeeId] || [];
          const isChairForThis = isChairOfCommittee(committee);
          const reportStatus =
            reportStatuses[committeeId] ||
            (statusesLoading
              ? { label: 'Loading…', variant: 'secondary' }
              : { label: 'Not Started', variant: 'secondary' });

          return (
            <div key={idx} className="mb-4">
              <LazyCommitteeCard
                committee={committee}
                committeeMembers={committeeMembers}
                reportStatus={reportStatus}
                isChairForThis={isChairForThis}
                isAdmin={isAdmin}
                canDownloadPdf={
                  isAdmin || isCouncilMember || isSCSecretary || isChairForThis
                }
                editingCommitteeId={editingCommitteeId}
                onRequestEdit={handleRequestEdit}
                onDirtyChange={handleDirtyChange}
                onReportSaved={refetchStatuses}
                showNotification={showNotification}
                index={idx}
              />
            </div>
          );
        })
      )}
    </>
  );

  const adminContent = (
    <Tabs defaultActiveKey="rankings" className="mb-4">
      <Tab eventKey="rankings" title="Ranked Candidates">
        {rankingsContent}
      </Tab>
      <Tab eventKey="reminder-logs" title="Reminder Logs">
        <ChairReminderLogs />
      </Tab>
    </Tabs>
  );

  return (
    <>
      <PageTitle
        motherMenu="Administration"
        activeMenu="Ranked Candidates Report"
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
        notificationIcon={notificationIcon}
        notificationIconColor={notificationIconColor}
        notificationAction={notificationAction}
      />

      {isAdmin ? adminContent : rankingsContent}

      <ModalConfirmation
        showModal={showSwitchWarningModal}
        setShowModal={setShowSwitchWarningModal}
        title="Unsaved Changes"
        handleCancelSelect={handleCancelSwitch}
        bodyText="You have unsaved changes in the report you are currently editing. If you switch to another report, your changes will be lost.<br/><br/>Would you like to continue and discard your changes?"
        handleConfirmSelect={handleConfirmSwitch}
      />
    </>
  );
};

export default RankingReport;
